import {
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import TableCell from '@mui/material/TableCell';
import { toast } from 'react-toastify';

import { APIErrorT, APISuccessPaginationT } from 'shared/models/api';
import { APICallParams, APIInterfaceI } from 'utils/api';
import { usePrevious } from 'utils/react-hooks-custom';
import { generateRandomString } from 'utils/string';

import { ConditionalTooltip, ConditionalLink } from '../Conditional';
import ButtonLoadNextPage from './ButtonLoadNextPage';

export const RenderBoolean = (value: boolean, displayType: 'text' | 'icon' = 'text'): string | ReactNode => {
  if (displayType === 'icon') {
    return (value ? <CheckCircleOutlineIcon color="success" /> : <HighlightOffIcon color="error" />);
  }
  return (value ? 'Yes' : 'No');
};

export interface TableRenderFieldT {
  displayName: string;
  // eslint-disable-next-line no-unused-vars
  renderFn(row: any): string | ReactNode;
  // eslint-disable-next-line no-unused-vars
  tooltipFn?(row: any): string;
  // eslint-disable-next-line no-unused-vars
  linkFn?(row: any): string;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify' | undefined;
}
interface TableRenderProps {
  api: APIInterfaceI; // From utils/api
  apiParams?: APICallParams;
  renderFields: Array<TableRenderFieldT>;
  // eslint-disable-next-line no-unused-vars
  onClickRow?: (row: any) => void;
  defaultRowsPerPage?: number;
  forceRerender?: number;
  hasNoPagination?: boolean;
}
function TableRender({
  api,
  apiParams,
  renderFields,
  onClickRow,
  defaultRowsPerPage = 20,
  forceRerender,
  hasNoPagination = false,
}: TableRenderProps) {
  const [listIsLoading, setListIsLoading] = useState(true);
  const [list, setList] = useState<Array<any>>([]);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);
  const firstRowOfNewPageRef = useRef<HTMLTableRowElement>(null);

  const preForceRerender = usePrevious({ forceRerender });

  const fetchData = (cursor = 0) => {
    api.getAll(
      {
        cursor, limit: defaultRowsPerPage,
      },
      apiParams,
    )
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              setListIsLoading(false);
              setIsLoadingMoreData(false);
            });
        } else {
          resp.json()
            .then((data: APISuccessPaginationT<any> | any) => {
              let { result } = data;
              if (!result) {
                result = data.users;
              }
              if (!result) {
                result = data.organizations;
              }
              if (!result) {
                result = [];
              }
              setList((items) => [...items, ...result]);
              setListIsLoading(false);
              setIsLoadingMoreData(false);
              if (result.length < defaultRowsPerPage || hasNoPagination) {
                setHasMoreData(false);
              } else {
                setHasMoreData(true);
              }
              if (cursor > 0) {
                // Scroll to the first row of the new page
                firstRowOfNewPageRef?.current?.scrollIntoView({ behavior: 'smooth' });
              }
            });
        }
      });
  };

  const loadNextPage = (cursor: number) => {
    fetchData(cursor);
    setIsLoadingMoreData(true);
  };

  useEffect(() => {
    if (forceRerender !== preForceRerender) {
      setList([]);
    }
    setListIsLoading(true);
    fetchData();
  }, [apiParams?.deviceProfileId, forceRerender]); // Force rerender on property change

  return (
    <>
      <TableContainer component={Paper} className="mui-table-custom">
        <Table stickyHeader aria-label="table">
          {
            (!listIsLoading && !list.length) || listIsLoading
              ? (
                <caption>
                  {
                    listIsLoading
                      ? 'Loading the data...'
                      : 'No data to display'
                  }
                </caption>
              )
              : null
          }
          <TableHead>
            <TableRow>
              {
                renderFields.map((field) => (
                  <TableCell
                    key={generateRandomString()}
                    align={field.align}
                  >
                    { field.displayName }
                  </TableCell>
                ))
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((row, index) => {
              // We targer index + 1 because there are a bug :
              //   > scrollIntoView is scrolling at the bottom of the target
              const isFirstRowOfNewPage = (
                list.length - (list.length % defaultRowsPerPage || defaultRowsPerPage) === index + 1
              );
              return (
                <TableRow
                  key={generateRandomString()}
                  style={{ cursor: onClickRow ? 'pointer' : 'default' }}
                  onClick={() => onClickRow && onClickRow(row)}
                  ref={isFirstRowOfNewPage ? firstRowOfNewPageRef : null}
                >
                  {
                    renderFields.map((field) => {
                      const displayValue = field.renderFn(row);
                      return (
                        <ConditionalTooltip key={`${row.id}.${field.displayName}`} title={field.tooltipFn && field.tooltipFn(row)}>
                          <TableCell align={field.align}>
                            <ConditionalLink link={field.linkFn && field.linkFn(row)}>
                              { displayValue || '·' }
                            </ConditionalLink>
                          </TableCell>
                        </ConditionalTooltip>
                      );
                    })
                  }
                </TableRow>
              );
            })}
          </TableBody>
          {
            hasMoreData === true
              ? null
              : <caption style={{ userSelect: 'none' }} />
          }
        </Table>
      </TableContainer>
      <ButtonLoadNextPage
        rowsPerPage={defaultRowsPerPage}
        hasMoreData={hasMoreData}
        isLoadingMoreData={isLoadingMoreData}
        onLoadNextPage={loadNextPage}
      />
    </>
  );
}

export default TableRender;
